import { useTranslation } from "react-i18next";
import { updateDokobitSettings } from "services/company.service";
import { CompanyEntity } from "types/Company";
import { Field, FIELD_INPUT_CLASS } from "../catalog-form-parts";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  Toggle,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";

// The server's codes for a refused save, and the text each one gets.
const DOKOBIT_ERROR_KEYS = {
  DOKOBIT_DATA_REQUIRED: "settings_signing_dokobit_key_required",
  DOKOBIT_KEY_INVALID: "settings_signing_dokobit_key_too_short",
};

interface DokobitConnectTabProps {
  companyId: string;
  /** The key arrives as its last 4 characters only. */
  dokobit: CompanyEntity["dokobit"];
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

export default function DokobitConnectTab({
  companyId,
  dokobit,
  onRefresh,
}: DokobitConnectTabProps) {
  const { t } = useTranslation();
  // The draft is the key as typed. It starts empty: the stored key is never shown.
  const keyEdit = useInlineEdit("");
  const switchSave = useSaveWithToast();
  const keySave = useSaveWithToast();

  const hasKey = Boolean(dokobit?.accessKey);
  // With no key saved, switching on opens the key editor (there is nothing to
  // save yet), and saving that first key also switches Dokobit on.
  const isOn = Boolean(dokobit?.isActive) || (keyEdit.editing && !hasKey);

  const send = (changes: {
    accessKey?: string;
    isDokobitSignActive: boolean;
  }) => updateDokobitSettings(changes, companyId).then(onRefresh);
  const saveOptions = {
    errorMessage: (err: unknown) => t(saveErrorKey(err, DOKOBIT_ERROR_KEYS)),
  };

  const changeSwitch = async (enabled: boolean) => {
    if (!hasKey) {
      if (enabled) keyEdit.start();
      else keyEdit.cancel();
      return;
    }
    await switchSave.save(
      () => send({ isDokobitSignActive: enabled }),
      saveOptions
    );
  };

  const saveKey = async () => {
    const saved = await keySave.save(
      () =>
        send({ accessKey: keyEdit.draft.trim(), isDokobitSignActive: isOn }),
      saveOptions
    );
    if (saved) keyEdit.cancel();
  };

  return (
    <SettingsCard
      title="Dokobit"
      description={t("settings_signing_dokobit_description")}
      actions={
        <Toggle
          label={t("settings_signing_dokobit_switch")}
          checked={isOn}
          disabled={switchSave.saving || keySave.saving}
          onChange={changeSwitch}
        />
      }
    >
      <SettingRow
        label={t("settings_signing_key_label")}
        description={t("settings_signing_dokobit_key_hint")}
        value={
          hasKey
            ? t("settings_secret_entered_last4", { last4: dokobit?.accessKey })
            : t("settings_none")
        }
        action={!keyEdit.editing && <ChangeLink onClick={keyEdit.start} />}
      >
        {keyEdit.editing && (
          <EditPanel
            saving={keySave.saving}
            error={keySave.error}
            onSave={saveKey}
            onCancel={keyEdit.cancel}
          >
            <Field htmlFor="dokobit-key" label={t("dokobit_acess_key")}>
              <input
                id="dokobit-key"
                type="password"
                autoComplete="new-password"
                className={`${FIELD_INPUT_CLASS} max-w-md`}
                value={keyEdit.draft}
                placeholder={
                  hasKey ? t("settings_secret_keep_placeholder") : undefined
                }
                onChange={(e) => keyEdit.setDraft(e.target.value)}
              />
            </Field>
          </EditPanel>
        )}
      </SettingRow>
    </SettingsCard>
  );
}
